import type { DirectiveBinding } from 'vue/types/options';
import { resolveMediaUrl, releaseMediaUrl } from '../secureMediaService';

type SecureMediaMode = 'src' | 'background';

export type SecureMediaValue = string | null | undefined | {
    src: string | null | undefined;
    /** 加载失败 / src 为空时回退到的占位 URL（不会被释放） */
    fallback?: string;
};

interface ISecureMediaState {
    url: string | null;
    fallback: string | null;
    mode: SecureMediaMode;
    blobUrl: string | null;
    requestId: number;
}

interface ISecureMediaElement extends HTMLElement {
    __secureMediaState__?: ISecureMediaState;
}

const STATE_KEY = '__secureMediaState__';

const resolveMode = ( binding: DirectiveBinding ): SecureMediaMode => (
    binding.arg === 'background' ? 'background' : 'src'
);

const normalizeValue = ( value: SecureMediaValue ): { url: string | null; fallback: string | null; } => {

    if ( !value ) {

        return { url: null, fallback: null };

    }
    if ( typeof value === 'string' ) {

        return { url: value, fallback: null };

    }
    return {
        url: value.src ?? null,
        fallback: value.fallback ?? null
    };

};

const applyDirectSrc = ( el: ISecureMediaElement, mode: SecureMediaMode, url: string ): void => {

    if ( mode === 'background' ) {

        el.style.backgroundImage = url ? `url("${url}")` : '';
        return;

    }
    if ( el instanceof HTMLImageElement ) {

        el.src = url;
        return;

    }
    el.setAttribute( 'src', url );

};

const releaseStored = ( el: ISecureMediaElement ): void => {

    const state = el[STATE_KEY];
    if ( state?.blobUrl ) {

        releaseMediaUrl( state.blobUrl );
        state.blobUrl = null;

    }

};

const applyFallbackOrEmpty = ( el: ISecureMediaElement, state: ISecureMediaState ): void => {

    applyDirectSrc( el, state.mode, state.fallback ?? '' );

};

const isStateAlive = ( el: ISecureMediaElement, state: ISecureMediaState, reqId: number ): boolean => (
    el[STATE_KEY] === state && state.requestId === reqId
);

const apply = async ( el: ISecureMediaElement, state: ISecureMediaState ): Promise<void> => {

    if ( !state.url ) {

        applyFallbackOrEmpty( el, state );
        return;

    }

    // 立即展示 fallback 作为占位，避免异步等待期间的空白/闪烁
    if ( state.fallback ) {

        applyFallbackOrEmpty( el, state );

    }

    const currentReq = ++state.requestId;

    // SW 可用 → 原 URL；不可用 → 降级 blob URL；失败 → fallback/空
    const finalUrl = await resolveMediaUrl( state.url, { mode: state.mode });

    if ( !isStateAlive( el, state, currentReq )) {

        // 旧请求被抢占，释放可能新生成的 blob URL
        if ( finalUrl && finalUrl !== state.url ) {

            releaseMediaUrl( finalUrl );

        }
        return;

    }

    if ( finalUrl === null && state.fallback ) {

        applyFallbackOrEmpty( el, state );
        return;

    }

    if ( finalUrl === null ) {

        // 失败且无 fallback：保持当前状态不变
        return;

    }

    if ( state.blobUrl && state.blobUrl !== finalUrl ) {

        releaseMediaUrl( state.blobUrl );

    }
    state.blobUrl = finalUrl !== state.url ? finalUrl : null;
    applyDirectSrc( el, state.mode, finalUrl );

};

/**
 * `v-secure-media` 自定义指令。
 *
 * 用法：
 * - `<img v-secure-media="url">`：等效 `<img :src="url">`，但 SW 不可用时自动走主线程
 *   `fetch + 解密 + Blob URL` 的兜底
 * - `<div v-secure-media:background="url">`：等效设置 `background-image: url(...)`，
 *   兼容现有 `:style` / `v-lazy:background-image` 的使用模式
 * - 对象形式 `v-secure-media:background="{ src, fallback }"`：加载失败 / src 为空时
 *   使用 `fallback` 占位（不会被释放）
 *
 * 业务层无需感知 SW 是否可用，更不用关心解密细节；指令在 `update`/`unbind`
 * 时会自动 `URL.revokeObjectURL` 释放 Blob 资源。
 */
export const secureMediaDirective = {

    bind( el: any, binding: any ): void {

        const target = el as ISecureMediaElement;
        const mode = resolveMode( binding );
        const { url, fallback } = normalizeValue( binding.value as SecureMediaValue );
        const state: ISecureMediaState = { url, fallback, mode, blobUrl: null, requestId: 0 };
        target[STATE_KEY] = state;
        apply( target, state ).catch( console.error );

    },

    update( el: any, binding: any ): void {

        const target = el as ISecureMediaElement;
        const mode = resolveMode( binding );
        const { url, fallback } = normalizeValue( binding.value as SecureMediaValue );
        const state = target[STATE_KEY];

        if ( state && state.url === url && state.fallback === fallback && state.mode === mode ) {

            return;

        }

        releaseStored( target );
        const next: ISecureMediaState = {
            url,
            fallback,
            mode,
            blobUrl: null,
            requestId: state?.requestId ?? 0
        };
        target[STATE_KEY] = next;
        apply( target, next ).catch( console.error );

    },

    unbind( el: any ): void {

        const target = el as ISecureMediaElement;
        if ( target[STATE_KEY] ) {

            target[STATE_KEY]!.requestId++;

        }
        releaseStored( target );
        delete target[STATE_KEY];

    }

};
